import React, { useState, useEffect, useRef } from 'react'
import { Form, Button } from 'react-bootstrap'

function MultiSelectFilterPopover({
  column,
  params,
  setParams,
  options = [],
  onClose,
  onSetHandleApply,
  canEdit = true,
  hideApplyButton = false
}) {
  const accessorKey = column.accessor_key
  const modeKey = `${accessorKey}Mode`

  // Options are supplied by the caller from the table_columns API response.
  const filterOptions = options
  const [currentValues, setCurrentValues] = useState(
    params[accessorKey]
      ? Array.isArray(params[accessorKey])
        ? params[accessorKey]
        : [params[accessorKey]]
      : []
  )
  const [mode, setMode] = useState(params[modeKey] || 'include')

  // Track initial values to detect changes
  const initialValues = useRef(
    params[accessorKey]
      ? Array.isArray(params[accessorKey])
        ? [...params[accessorKey]]
        : [params[accessorKey]]
      : []
  )
  const initialMode = useRef(params[modeKey] || 'include')

  const handleChange = (optionValue) => {
    if (!canEdit) return
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter((v) => v !== optionValue)
      : [...currentValues, optionValue]

    setCurrentValues(newValues)
  }

  const handleSelectAll = () => {
    if (!canEdit) return
    const allValues = filterOptions.map((opt) => opt.value)
    setCurrentValues(allValues)
  }

  const handleApply = () => {
    // Only update params if values or mode have changed
    const hasChanges = currentValues.length !== initialValues.current.length ||
                       !currentValues.every((val, idx) => val === initialValues.current[idx]) ||
                       mode !== initialMode.current

    if (hasChanges) {
      setParams((p) => ({ ...p, [accessorKey]: currentValues, [modeKey]: mode }))
    }
    onClose()
  }

  useEffect(() => {
    if (onSetHandleApply) {
      onSetHandleApply(handleApply)
    }
  }, [currentValues, mode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply changes when hideApplyButton is true
  useEffect(() => {
    if (hideApplyButton) {
      setParams((p) => ({ ...p, [accessorKey]: currentValues, [modeKey]: mode }))
    }
  }, [currentValues, mode, hideApplyButton, setParams, accessorKey, modeKey])

  const handleClear = () => {
    setCurrentValues([])
    setMode('include')
    setParams((p) => ({ ...p, [accessorKey]: [], [modeKey]: 'include' }))
  }


  return (
    <div>
      <div className="mb-3">
        <strong>{column.name}</strong>
      </div>

      <div className="mb-3">
        <Form.Select
          value={mode}
          onChange={(e) => canEdit && setMode(e.target.value)}
          size="sm"
          disabled={!canEdit}
        >
          <option value="include">Include selected</option>
          <option value="exclude">Exclude selected</option>
        </Form.Select>
      </div>

      <div className="mb-3">
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          onClick={handleSelectAll}
          disabled={!canEdit}
        >
          Select All
        </Button>
        <Button variant="light" size="sm" onClick={handleClear} disabled={!canEdit}>
          Clear All
        </Button>
      </div>

      <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="mb-3">
        {filterOptions.map((option) => (
          <Form.Check
            key={option.value}
            type="checkbox"
            id={`${accessorKey}-${option.value}`}
            label={option.label}
            checked={currentValues.includes(option.value)}
            onChange={() => handleChange(option.value)}
            className="mb-1 small"
            disabled={!canEdit}
          />
        ))}
      </div>

      {!hideApplyButton && (
        <div className="d-flex justify-content-between align-items-center gap-2">
          <span className="text-muted small">
            {currentValues.length} selected
          </span>
          {canEdit && (
            <Button variant="light" size="sm" onClick={handleClear}>
              Clear
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={canEdit ? handleApply : onClose}>
            {canEdit ? 'Apply' : 'Close'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default MultiSelectFilterPopover
